import {
  Alert,
  Avatar,
  Box,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import type { BlockedUserResponse } from "@bw/shared/types/generated";
import { Link as RouterLink } from "react-router-dom";

import { Page } from "../../../common/components/Page";
import { ListSkeleton } from "../components/ListSkeleton";
import { useBlockedUsers, useUnblockPlayer } from "../hooks/useBlockedUsers";

function safeErrorMessage(
  error: unknown,
  fallback: string,
  hiddenIdentity?: string,
): string {
  if (!(error instanceof Error) || !error.message.trim()) return fallback;
  if (hiddenIdentity && error.message.includes(hiddenIdentity)) return fallback;
  return error.message;
}

function BlockedPlayerRow({ player }: { player: BlockedUserResponse }) {
  const unblock = useUnblockPlayer(player.user_id);
  const displayName = player.display_name.trim() || "Unknown";
  return (
    <ListItem
      alignItems="flex-start"
      divider
      secondaryAction={
        <Button
          aria-label={`${unblock.isPending ? "Unblocking" : "Unblock"} ${displayName}`}
          disabled={unblock.isPending}
          onClick={() => unblock.mutate()}
          size="small"
          variant="outlined"
        >
          {unblock.isPending ? "Unblocking…" : "Unblock"}
        </Button>
      }
      sx={{ pr: 16 }}
    >
      <ListItemAvatar>
        <Avatar alt={displayName} src={player.avatar_url ?? undefined}>
          {displayName === "Unknown" ? "?" : displayName.charAt(0)}
        </Avatar>
      </ListItemAvatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <ListItemText primary={displayName} />
        {unblock.isError ? (
          <Alert role="alert" severity="error" sx={{ mt: 1 }}>
            {safeErrorMessage(
              unblock.error,
              "Could not unblock this player.",
              player.user_id,
            )}
          </Alert>
        ) : null}
      </Box>
    </ListItem>
  );
}

/** Reachable recovery surface for the viewer's outgoing block list. */
export function BlockedUsersPage() {
  const { blockedUsers } = useBlockedUsers();
  const rows = blockedUsers.data ?? [];

  return (
    <Page title="Blocked players">
      <Stack spacing={2}>
        <Button
          component={RouterLink}
          to="/me/account"
          sx={{ alignSelf: "flex-start" }}
        >
          Back to Account
        </Button>

        {blockedUsers.isError ? (
          <Alert
            action={
              <Button
                color="inherit"
                onClick={() => blockedUsers.refetch()}
                size="small"
              >
                Try again
              </Button>
            }
            role="alert"
            severity="error"
          >
            {safeErrorMessage(
              blockedUsers.error,
              "Blocked players are unavailable.",
            )}
          </Alert>
        ) : blockedUsers.isLoading ? (
          <Box
            aria-label="Loading blocked players"
            aria-live="polite"
            role="status"
          >
            <ListSkeleton />
          </Box>
        ) : rows.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
            <Typography color="text.secondary">
              You haven&apos;t blocked anyone.
            </Typography>
          </Paper>
        ) : (
          <List component={Paper} disablePadding variant="outlined">
            {rows.map((player) => (
              <BlockedPlayerRow key={player.user_id} player={player} />
            ))}
          </List>
        )}
      </Stack>
    </Page>
  );
}
